import React, { useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  ClickAwayListener,
  FormControl,
  List,
  ListItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { ArrowDropDown, ArrowDropUp, ClearRounded } from "@material-ui/icons";

import { PropChipType, sortPropsFn } from "./PropChips";
import FormLabelWithTooltip from "../FormLabelWithTooltip";

export type AllowListProp = {
  destination: string;
  destinationId: string;
  displayName: string | undefined;
  label: string;
  value: string;
  type: PropChipType;
};

export type PropsFilterSelectProps = {
  label: string;
  allOption: AllowListProp & { value: typeof ALL_OPTION_VALUE };
  props: AllowListProp[];
  selectedProps: Set<AllowListProp>;
  onChange: (selectedProps: Set<AllowListProp>) => void;
  onClickClearButton?: () => void;
};

const useStyles = makeStyles((theme) => ({
  checkbox: {
    paddingLeft: theme.spacing(0.75),
  },
  clearButton: {
    background: theme.palette.grey[300],
    borderRadius: "100%",
    fontSize: "1em",
    height: "1.25em",
    marginLeft: "-0.125em",
    marginTop: "-0.125em",
    padding: "0.125em",
    width: "1.25em",

    "&:hover": {
      background: theme.palette.grey[400],
    },
  },
  formControl: {
    position: "relative" as "relative",
    textOverflow: "ellipsis",
    textTransform: "none" as "none",
    whiteSpace: "nowrap" as "nowrap",
  },
  option: {
    maxHeight: theme.spacing(4.5),
    padding: theme.spacing(0.5, 0),
    textOverflow: "ellipsis",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 0, 0.1)",
      cursor: "pointer",
    },
  },
  optionList: {
    padding: 0,
  },
  optionLabel: {
    height: theme.spacing(4),
    lineHeight: "1.125rem",
    overflow: "hidden",
    padding: theme.spacing(1, 0, 1, 4),
    position: "absolute" as "absolute",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    width: "100%",
  },
  optionsMenu: {
    maxHeight: theme.spacing(30),
    left: 0,
    overflowY: "scroll",
    padding: theme.spacing(0.25),
    position: "absolute" as "absolute",
    top: theme.spacing(6.925),
    width: "100%",
    zIndex: 1001,
  },
  searchInput: {},
  trigger: {
    overflow: "hidden",
    padding: theme.spacing(1),
    textOverflow: "ellipsis",
    textTransform: "none",
    whiteSpace: "nowrap",
    width: "100%",

    "& .MuiButton-label": {
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textAlign: "left",
      display: "block",
    },
  },
  triggerCaret: {
    position: "absolute" as "absolute",
    right: 0,
    textAlign: "right",
    top: "0.25em",
    height: "1.5em",
    width: "1.25em",
  },
  triggerLabel: {
    fontWeight: 400,
    overflow: "hidden",
    paddingRight: "2.5em",
    position: "relative" as "relative",
    textAlign: "left",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  clearButtonWrapper: {
    position: "absolute" as "absolute",
    right: "1em",
    textAlign: "right",
    top: 0,
    height: "1.5em",
    width: "1.25em",
  },
}));

export const ALL_OPTION_VALUE = "___ALL_OPTION___";

function PropsFilterSelect({
  label,
  allOption,
  props = [],
  selectedProps,
  onChange,
  onClickClearButton = () => {},
}: PropsFilterSelectProps): JSX.Element {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [propsSearchFilter, setPropsSearchFilter] = useState<string>("");

  const selectedPropsValues = useMemo(() => {
    return new Set([...selectedProps].map((p) => p.value));
  }, [selectedProps]);

  const areAllOptionsSelected = useMemo(() => {
    return (
      selectedProps.has(allOption) ||
      (selectedProps.size === props.length && props.length > 0)
    );
  }, [selectedProps, allOption, props.length]);

  const filteredProps = useMemo(() => {
    const searchLowerCase = propsSearchFilter.toLowerCase();

    return props
      .filter((p) => {
        return p.label.toLowerCase().indexOf(searchLowerCase) !== -1;
      })
      .sort(sortPropsFn);
  }, [props, propsSearchFilter]);

  const handleAllOptionClick = () => {
    if (areAllOptionsSelected) {
      onChange(new Set([]));
    } else {
      onChange(new Set([allOption]));
    }
  };

  const handleOptionClick = (option: AllowListProp) => {
    const isAdding = !selectedPropsValues.has(option.value);

    if (isAdding) {
      onChange(
        new Set([...selectedProps, option].filter((p) => p !== allOption))
      );
    } else {
      onChange(
        new Set(
          [...selectedProps].filter(
            (p) => p.value !== option.value && p !== allOption
          )
        )
      );
    }
  };

  const selectedPropsText = useMemo(() => {
    if (areAllOptionsSelected && props.length > 0) {
      return "All Filters Selected";
    }

    if (selectedProps.size === 0) {
      return "None Selected";
    }

    const numSelected = [...selectedProps].filter((p) => p !== allOption)
      .length;

    return `${numSelected} Filter${numSelected === 1 ? "" : "s"} Selected`;
  }, [allOption, areAllOptionsSelected, props, selectedProps]);

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <FormControl fullWidth>
        <FormLabelWithTooltip
          label={label}
          tooltip={`Filter HIPAA Allowlists by ${label}`}
        />

        <Tooltip
          title={
            props.length > 0 ? "Show Selected Filters" : "Nothing to Select"
          }
        >
          {/* The span below only serves as an event target in the case the button element below is disabled. */}
          {/* This is because when buttons are disabled, they cease to be event targets, and without an event target, */}
          {/* the Tooltip above will not work. */}
          <span>
            <Button
              fullWidth
              className={classes.trigger}
              disabled={props.length === 0}
              endIcon={
                isOpen ? (
                  <ArrowDropUp
                    className={classes.triggerCaret}
                    fontSize="small"
                  />
                ) : (
                  <ArrowDropDown
                    className={classes.triggerCaret}
                    fontSize="small"
                  />
                )
              }
              onClick={() => setIsOpen(true)}
              size="small"
              variant="outlined"
            >
              <div
                className={classes.triggerLabel}
                style={{
                  color: selectedProps.size === 0 ? "#888" : "inherit",
                  paddingRight: selectedProps.size === 0 ? "1.25em" : "2.5em",
                }}
              >
                {selectedPropsText}
                {selectedProps.size > 0 && props.length > 0 && (
                  <div className={classes.clearButtonWrapper}>
                    <Tooltip title="Clear Filters" placement="top" arrow>
                      <div
                        role="button"
                        onClickCapture={(e) => {
                          e.stopPropagation();

                          onClickClearButton();
                        }}
                      >
                        <ClearRounded
                          className={classes.clearButton}
                          fontSize="small"
                        />
                      </div>
                    </Tooltip>
                  </div>
                )}
              </div>
            </Button>
          </span>
        </Tooltip>

        {isOpen && props.length > 0 && (
          <Paper className={classes.optionsMenu} elevation={3}>
            <TextField
              autoFocus
              fullWidth
              placeholder="Filter props..."
              size="small"
              variant="outlined"
              onFocus={() => setIsOpen(true)}
              onChange={(e) => {
                setPropsSearchFilter(e.target.value);
              }}
              onBlur={() => setPropsSearchFilter("")}
            />

            <List className={classes.optionList}>
              <Tooltip title="Select All">
                <ListItem className={classes.option}>
                  <Checkbox
                    className={classes.checkbox}
                    checked={areAllOptionsSelected}
                    size="small"
                    onClick={() => {
                      handleAllOptionClick();
                    }}
                  />{" "}
                  <Typography
                    className={classes.optionLabel}
                    component="div"
                    variant="body1"
                    onClick={() => handleAllOptionClick()}
                  >
                    {allOption.label}
                  </Typography>
                </ListItem>
              </Tooltip>
              {filteredProps.map((p) => (
                <Tooltip
                  title={p.label}
                  key={`props-filter-select-option-${label}-${p.value}`}
                >
                  <ListItem className={classes.option}>
                    <Checkbox
                      className={classes.checkbox}
                      checked={
                        !areAllOptionsSelected &&
                        selectedPropsValues.has(p.value)
                      }
                      size="small"
                      onClick={() => {
                        handleOptionClick(p);
                      }}
                    />{" "}
                    <Typography
                      className={classes.optionLabel}
                      component="div"
                      variant="body1"
                      onClick={() => handleOptionClick(p)}
                    >
                      {p.label}
                    </Typography>
                  </ListItem>
                </Tooltip>
              ))}
            </List>
          </Paper>
        )}
      </FormControl>
    </ClickAwayListener>
  );
}

export default PropsFilterSelect;
